/**
 * fissalFallback.js — Fissal-voiced fallback messages for the chat
 * surfaces (FissalBubble, FissalAI widget).
 *
 * Issue #294: Fissal sometimes returned an empty or surfaced-error
 * response and the chat showed nothing useful (or the terse string
 * "(no reply)"). Every error path in the client should surface a short,
 * warm, in-character acknowledgement so the user always knows Fissal
 * heard them and can retry.
 *
 * Voice rules (fissal-voice-curator):
 *   - Whimsy budget: one short aside per message max
 *   - Jone tone: short, bright, fast — quick quips only
 *   - Never expose raw stack traces or HTTP status codes to the user
 *
 * All exported strings are static — no model calls, no template
 * literals carrying user content (CSP-safe, RAG-safe).
 */

const RATE_LIMIT_CUE =
  '*paws raised* — Jone is pinging too rapidly. Allow the escapement wheel to cool, then try again?';

const SERVER_ERROR_CUE =
  '*gears tangled* — brass wheels locked mid-thought. Try once more in a breath?';

const NETWORK_ERROR_CUE =
  '*ear flick* — transceiver lost the tonal pitch. Try once more?';

const EMPTY_REPLY_CUE =
  '*tail flick* — Jode hummed but no words came through. Ask again, perhaps differently?';

const IMAGE_UNREADABLE_CUE =
  'This one cannot read images — I see the file name `pasted_screenshot.png` but have no vision/OCR tool. Please describe the content in text, or ask about data I can query (prices, sync, diagnostics).';

const AUTH_ERROR_CUE =
  '*tilts head* — Fissal can\'t see who you are. Re-link your ESO tag and try again?';

/**
 * Pick a Fissal-voiced fallback for a non-OK HTTP response.
 *
 * @param {number} status   HTTP status code
 * @param {object} body     parsed JSON body (may have .error)
 * @returns {string}        in-voice fallback
 */
export function fissalHttpFallback(status, body) {
  // Prefer the server's own message when it's already in voice (the
  // backend uses Fissal-voiced strings for 429 / 500 — keep them).
  if (body && typeof body.error === 'string' && body.error.trim()) {
    return body.error;
  }
  if (status === 429) return RATE_LIMIT_CUE;
  if (status === 400 || status === 401 || status === 403) return AUTH_ERROR_CUE;
  if (status >= 500) return SERVER_ERROR_CUE;
  return SERVER_ERROR_CUE;
}

/** Network-level (fetch threw) fallback. */
export function fissalNetworkFallback() {
  return NETWORK_ERROR_CUE;
}

/**
 * Fallback for a 200 OK whose body had no usable text. Distinct from
 * an HTTP error so the wording can hint "ask differently" rather than
 * "try again" — the request reached Fissal, the answer just didn't
 * land.
 */
export function fissalEmptyFallback() {
  return EMPTY_REPLY_CUE;
}

/**
 * Returns true if the cleaned model output is effectively empty
 * (whitespace, lone punctuation, or the legacy '(no reply)' sentinel).
 */
export function isEmptyFissalReply(text) {
  if (!text || typeof text !== 'string') return true;
  const t = text.trim();
  if (!t || t.length < 3) return true;
  if (t === '(no reply)') return true;
  // Strip simple punctuation/whitespace; if nothing's left, it's empty.
  // Also catches bare '#' or '##' left by heading-URL degenerations (#330).
  if (!t.replace(/[\s.\-_*~`#]+/g, '')) return true;
  return false;
}
